/**
 * Regex Utilities
 * 
 * Centralized regex patterns and utilities for text operations
 */

/**
 * Escape special regex characters in a string
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
export const escapeRegex = (text) => {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Normalize text that came from a browser selection so it can be reliably
 * matched against `block.text`.
 *
 * The Slate editor renders every word as an inline <span>, and browsers /
 * Slate inject zero-width markers (\uFEFF, \u200B, ...) between those inline
 * elements to support cursor placement. When the user selects across multiple
 * word spans, `window.getSelection().toString()` can therefore contain:
 *   - zero-width characters (\u200B, \u200C, \u200D, \uFEFF)
 *   - non-breaking spaces (\u00A0) instead of regular spaces
 *   - collapsed/duplicated whitespace or newlines between inline elements
 *
 * We convert every one of those to a regular space so the downstream
 * tokenizer can split cleanly on whitespace.
 */
const normalizeSearchText = (text) => {
  return String(text || '')
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, ' ')
    .replace(/\u00A0/g, ' ');
};

/**
 * Create word boundary regex pattern.
 *
 * Two reasons this is not a plain `\b...\b`:
 *
 * 1. JavaScript's `\b` only recognizes ASCII word characters ([A-Za-z0-9_]).
 *    Without a Unicode-aware boundary, searches for Cyrillic
 *    (e.g. "Србија"), Serbian Latin with diacritics (š, č, ć, đ, ž), Arabic,
 *    Chinese, etc. never match.
 * 2. A selection made inside the Slate editor can come back with NBSPs, zero-
 *    width separators, or collapsed whitespace between words (see
 *    `normalizeSearchText`). Matching the search text literally against a
 *    clean `block.text` then silently fails and Replace All reports
 *    "0 occurrences" even though the phrase is clearly visible.
 *
 * We solve both by normalizing the input, splitting on whitespace, and
 * joining the tokens with `\s+` so any amount/kind of whitespace between
 * words in `block.text` will still match.
 *
 * @param {string} searchText - Text to search for
 * @param {boolean} caseSensitive - Whether search should be case-sensitive
 * @returns {RegExp} Regex pattern (guaranteed non-null; matches nothing if empty)
 */
export const createWordBoundaryRegex = (searchText, caseSensitive = false) => {
  const cleaned = normalizeSearchText(searchText);
  const tokens = cleaned.split(/\s+/).filter(Boolean);
  const flags = caseSensitive ? 'gu' : 'giu';
  const wordChar = '[\\p{L}\\p{N}_]';
  if (tokens.length === 0) {
    return new RegExp('(?!)', flags);
  }
  const pattern = tokens.map(escapeRegex).join('\\s+');
  return new RegExp(`(?<!${wordChar})${pattern}(?!${wordChar})`, flags);
};

/**
 * Create simple regex pattern (no word boundaries).
 *
 * Same whitespace/zero-width normalization as `createWordBoundaryRegex` so
 * the plain Replace behaves consistently with Replace All when the search
 * text comes from a Slate selection.
 *
 * @param {string} searchText - Text to search for
 * @param {boolean} caseSensitive - Whether search should be case-sensitive
 * @returns {RegExp} Regex pattern
 */
export const createSimpleRegex = (searchText, caseSensitive = false) => {
  const cleaned = normalizeSearchText(searchText);
  const tokens = cleaned.split(/\s+/).filter(Boolean);
  const flags = caseSensitive ? 'gu' : 'giu';
  if (tokens.length === 0) {
    return new RegExp('(?!)', flags);
  }
  const pattern = tokens.map(escapeRegex).join('\\s+');
  return new RegExp(pattern, flags);
};

export default {
  escapeRegex,
  createWordBoundaryRegex,
  createSimpleRegex,
};

